import isEmpty from 'lodash/isEmpty';
import {
  ALL_MODULES_FILTER,
  EMAIL_ATTRIBUTES_NAME,
  ImageInfoConfig,
  recommendImageInfoConfigs,
} from '../constants/common';
import { translate } from '../localization';
import { ContentTypeImage, ImageRecomendationInfoType } from '../types/Content';
import { CommonDataType, EmailModulesData } from '../types/EmailModules';
import { ImageRecommendationStatus } from '../types/Images';
import { Option } from '../types/SelectInput';
import { contentParamsSegment } from '../store/markettingContentGeneration/stateSegments/paramsSegment';
import store from '../store/markettingContentGeneration';

const SESSION_RESTORE_LOADING_KEY = 'isSessionRestoreLoading';

type FieldWithPossiblyUndefined<T, Key> =
  | GetFieldType<Exclude<T, undefined>, Key>
  | Extract<T, undefined>;

type GetIndexedField<T, K> = K extends keyof T
  ? T[K]
  : K extends `${number}`
    ? '0' extends keyof T // tuples have string keys, return undefined if K is not in tuple
      ? undefined
      : number extends keyof T
        ? T[number]
        : undefined
    : undefined;

export type GetFieldType<T, P> = P extends `${infer Left}.${infer Right}`
  ? Left extends keyof T
    ? FieldWithPossiblyUndefined<T[Left], Right>
    : Left extends `${infer FieldKey}[${infer IndexKey}]`
      ? FieldKey extends keyof T
        ? FieldWithPossiblyUndefined<
            | GetIndexedField<Exclude<T[FieldKey], undefined>, IndexKey>
            | Extract<T[FieldKey], undefined>,
            Right
          >
        : undefined
      : undefined
  : P extends keyof T
    ? T[P]
    : P extends `${infer FieldKey}[${infer IndexKey}]`
      ? FieldKey extends keyof T
        ?
            | GetIndexedField<Exclude<T[FieldKey], undefined>, IndexKey>
            | Extract<T[FieldKey], undefined>
        : undefined
      : undefined;

export type OptionsType = {
  id: string;
  label: string;
  name: string;
};

export function getValue<
  TData,
  TPath extends string,
  TDefault = GetFieldType<TData, TPath>,
>(
  data: TData,
  path: TPath,
  defaultValue?: TDefault
): GetFieldType<TData, TPath> | TDefault {
  const value = path
    .split(/[.[\]]/)
    .filter(Boolean)
    /* eslint-disable */
    .reduce<GetFieldType<TData, TPath>>(
      (value, key) => (value as any)?.[key],
      data as any
    );

  return value !== undefined ? value : (defaultValue as TDefault);
}

export const generateUniqueId = (strLength: number): string => {
  const array = new Uint32Array(strLength);
  window.crypto.getRandomValues(array);
  return Array.from(array, (val: number) =>
    `0${val.toString(16)}`.slice(-2)
  ).join('');
};

export const getWFDetails = () => {
  const params = new URLSearchParams(window.location.search);
  const taskId = params.get('taskId') || '';
  const projectId = params.get('projectId') || '';
  return {
    isWFDetailsAvailable: taskId !== '' && projectId !== '',
    taskId,
    projectId,
  };
};

export const getPreselectedValue = (options: OptionsType[], value: string) =>
  options.find(
    (option: OptionsType) =>
      option.name?.toLowerCase() === value?.toLocaleLowerCase()
  )?.id || '';

export const getSelectedValueName = (
  options: OptionsType[],
  selectedName: string
) =>
  options.find(
    (option: OptionsType) =>
      option.name?.toLowerCase() === selectedName?.toLocaleLowerCase()
  )?.name || '';

export const getSelectedValueLabel = (
  options: OptionsType[],
  selectedName: string
) =>
  options.find(
    (option: OptionsType) =>
      option.name?.toLowerCase() === selectedName?.toLocaleLowerCase()
  )?.label || '';

export const getImageRecommendationStatus = (
  isImageLoading: boolean,
  images: ContentTypeImage[],
  isRequested: boolean,
  errorFetchingImages: boolean
): ImageRecommendationStatus => {
  if (errorFetchingImages) return ImageRecommendationStatus.ERROR;

  if (!isImageLoading && !isRequested && images.length === 0)
    return ImageRecommendationStatus.IDLE;

  if (!isImageLoading && isRequested && images.length === 0)
    return ImageRecommendationStatus.NO_IMAGES;

  if (isImageLoading) return ImageRecommendationStatus.LOADING;

  if (!isImageLoading && images.length > 0)
    return ImageRecommendationStatus.LOADED;
  return ImageRecommendationStatus.IDLE;
};

export const getImageRecommendationInfo = (
  isImageRecomendationAllowed: boolean,
  imagesLength: number,
  imageRecommendationStatus: ImageRecommendationStatus,
  noOfImagesExpected: number
): boolean => {
  return (
    isImageRecomendationAllowed &&
    imageRecommendationStatus === ImageRecommendationStatus.LOADED &&
    imagesLength < noOfImagesExpected
  );
};

export const getImageInfoConfig = (
  isImageInfoVisible: boolean,
  imagesLength: number,
  expectedNoOfImages: number
): ImageRecomendationInfoType => {
  if (
    isImageInfoVisible &&
    imagesLength > 0 &&
    imagesLength < expectedNoOfImages
  ) {
    return (
      recommendImageInfoConfigs.find(
        (el: ImageRecomendationInfoType) =>
          el.type === ImageInfoConfig.INSUFFICIENT_IMAGES
      ) ?? ({} as ImageRecomendationInfoType)
    );
  }

  return {} as ImageRecomendationInfoType;
};

export const getInitials = (fullName: string) => {
  // Check for empty param
  if (!fullName) return '';

  // Trim any leading or trailing whitespace from the name.
  const trimmedName = fullName.trim();

  // Split the name into words using spaces as delimiters.
  const words = trimmedName.split(' ');

  // Check if there are any words in the name.
  if (!words.length) {
    return ''; // Return empty string if no words found
  }

  // Extract the first letter from the first and last words.
  const firstLetter = words[0][0].toUpperCase();
  const lastLetter =
    (words.length > 1 && words?.[words.length - 1]?.[0]?.toUpperCase()) || '';

  // Combine the first letters to form the initials.
  return firstLetter + lastLetter;
};

export const getConvertedName = (name: string) =>
  name
    .split(' ')
    .map(word => word.toLowerCase().replace(/\(|\)/g, ''))
    .join('-');

export const createUniqueModuleKey = (
  name: string,
  count: number = 1
): string => {
  return `${getConvertedName(name)}_${count}`;
};

export const createModulesFilterOptions = (
  modules: EmailModulesData[]
): CommonDataType[] => {
  // Track seen labels to avoid duplicates
  const seenLabels = new Set<string>();
  let segmentCount = 0; // For generating unique ids

  const defaultFilterOption: CommonDataType[] = [
    {
      id: ALL_MODULES_FILTER,
      name: translate('all'),
      label: translate('all'),
    },
  ];

  return modules.reduce((acc: CommonDataType[], module) => {
    const { segment } = module;

    // If the label hasn't been processed yet, add it to the result array
    if (!isEmpty(segment) && !seenLabels.has(segment)) {
      segmentCount += 1;
      const newModule = {
        id: `${segment}-${segmentCount}`,
        label: segment,
        name: segment,
      };

      acc.push(newModule);
      seenLabels.add(segment); // Mark this label as processed
    }

    return acc;
  }, defaultFilterOption);
};

const getSegment = (name: string) => {
  if (name.indexOf('header') !== -1) return 'header';
  if (name.indexOf('body') !== -1) return 'body';
  if (name.indexOf('admin') !== -1) return 'admin';
  return '';
};

export const formatModulesResponse = (modules: CommonDataType[]) => {
  const segmentedModules: EmailModulesData[] = [];
  modules.forEach(module => {
    segmentedModules.push({
      ...module,
      isEmailAttributes: module.name === EMAIL_ATTRIBUTES_NAME,
      moduleKey: getConvertedName(module.name),
      segment: getSegment(module.name?.toLowerCase()),
    });
  });
  return segmentedModules;
};

export const getSessionId = () =>
  sessionStorage.getItem('pfz-cggenai-session-id') || '';

export const setSessionId = (sessionId: string) =>
  sessionStorage.setItem('pfz-cggenai-session-id', sessionId);

export const getContentSessionId = () =>
  sessionStorage.getItem('pfz-cggenai-content-session-id') || '';

export const setContentSessionId = (contentSessionId: string) =>
  sessionStorage.setItem('pfz-cggenai-content-session-id', contentSessionId);

export const getContentGroupId = () =>
  sessionStorage.getItem('pfz-cggenai-content-group-id') || '';

export const setContentGroupId = (contentGroupId: string) =>
  sessionStorage.setItem('pfz-cggenai-content-group-id', contentGroupId);
/**
 * Function to find a language other than the primary language.
 * @param languageList - An array of language objects conforming to the Option interface.
 * @returns The first language object found that does not match the primary language value, or null if all match.
 */
export const findNonPrimaryLanguage = (
  languageList: Option[]
): Option | null => {
  const { currentSelectedLanguage } = store.get(contentParamsSegment);
  // Iterate over each language object in the array
  for (const language of languageList) {
    // Check if the language value is not the primary language
    if (language.value !== currentSelectedLanguage) {
      // Return the language object if it doesn't match the primary language
      return language;
    }
  }
  // Return null if no non-primary language is found
  return null;
};
/*
 * Input date string YYYY-MM-DD
 * Output Date string e.g. 12 Mar 2025
 */
export const formatDate = (dateStr: string) => {
  const date = new Date(dateStr);
  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const day = date.getDate();
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();

  // Return the formatted date string
  return `${month} ${day}, ${year}`;
};

export const capitalizeFirstLetter = (str: string): string => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const setSessionRestoreLoading = (value: boolean): void => {
  sessionStorage.setItem(SESSION_RESTORE_LOADING_KEY, JSON.stringify(value));
};

export const clearSessionRestoreLoading = (): void => {
  sessionStorage.removeItem(SESSION_RESTORE_LOADING_KEY);
};

export const getSessionRestoreLoading = (): boolean => {
  const value = sessionStorage.getItem(SESSION_RESTORE_LOADING_KEY);
  return value ? JSON.parse(value) : false;
};

export const capitalizeWords = (str: string): string => {
  return str
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Function to find matching indication from options based on text content
 * @param sessionIndication - The indication text from session context
 * @param indicationOptions - Array of indication options from API
 * @returns The matching indication label or the original session indication if no match found
 */
export const getMatchingIndicationLabel = (
  sessionIndication: string,
  indicationOptions: { id: string; label: string; name?: string }[]
): string => {
  if (!sessionIndication || !indicationOptions?.length) {
    return sessionIndication || '';
  }

  // Normalize the session indication text (lowercase, remove extra spaces)
  const normalizedSessionIndication = sessionIndication.toLowerCase().trim();

  // Find matching indication option
  const matchedOption = indicationOptions.find(option => {
    const normalizedOptionName = option.name?.toLowerCase().trim() || '';
    const normalizedOptionLabel = option.label?.toLowerCase().trim() || '';

    return (
      normalizedOptionName === normalizedSessionIndication ||
      normalizedOptionLabel === normalizedSessionIndication
    );
  });

  // Return the matched option's label or original session indication
  return matchedOption?.label || sessionIndication;
};

/**
 * Function to find matching topics from options based on text content
 * @param sessionTopics - Array of topic texts from session context
 * @param topicOptions - Array of topic options from API
 * @returns Array of matching topic labels or original session topics if no matches found
 */
export const getMatchingTopicLabels = (
  sessionTopics: string[],
  topicOptions: { id: string; label: string; name?: string }[]
): string[] => {
  if (!sessionTopics?.length || !topicOptions?.length) {
    return sessionTopics || [];
  }

  return sessionTopics.map(sessionTopic => {
    // Normalize the session topic text (lowercase, remove extra spaces)
    const normalizedSessionTopic = sessionTopic.toLowerCase().trim();

    // Find matching topic option
    const matchedOption = topicOptions.find(option => {
      const normalizedOptionName = option.name?.toLowerCase().trim() || '';
      const normalizedOptionLabel = option.label?.toLowerCase().trim() || '';

      return (
        normalizedOptionName === normalizedSessionTopic ||
        normalizedOptionLabel === normalizedSessionTopic
      );
    });

    // Return the matched option's label or original session topic
    return matchedOption?.label || sessionTopic;
  });
};
